import { PaginatedResponse } from './index';

// Enum shared with backend (contracts.md)
export type LeadStato =
  | 'nuovo'
  | 'contattato'
  | 'qualificato'
  | 'convertito'
  | 'scartato';

export type LeadSource =
  | 'manual'
  | 'web_form'
  | 'import'
  | 'referral'
  | 'email'
  | 'phone'
  | 'event'
  | 'other';

export type LeadTipo = 'persona_fisica' | 'azienda';

export type LeadPrivacy = 'SI' | 'NO';

export const LEAD_PRIVACY_VALUES: LeadPrivacy[] = ['SI', 'NO'];

/**
 * Codice tipologia attività lead. Un tempo un set fisso, ora validato lato
 * backend contro l'anagrafica `activity_types` (tenant-scoped); qui resta
 * `string` per non dover ricompilare a ogni nuovo codice aggiunto da UI.
 */
export type LeadActivityType = string;

export const LEAD_STATI: LeadStato[] = [
  'nuovo',
  'contattato',
  'qualificato',
  'convertito',
  'scartato',
];

export const LEAD_SOURCES: LeadSource[] = [
  'manual',
  'web_form',
  'import',
  'referral',
  'email',
  'phone',
  'event',
  'other',
];

export const LEAD_STATO_LABEL: Record<LeadStato, string> = {
  nuovo: 'Nuovo',
  contattato: 'Contattato',
  qualificato: 'Qualificato',
  convertito: 'Convertito',
  scartato: 'Scartato',
};

export const LEAD_SOURCE_LABEL: Record<LeadSource, string> = {
  manual: 'Manuale',
  web_form: 'Web Form',
  import: 'Import',
  referral: 'Referral',
  email: 'Email',
  phone: 'Telefono',
  event: 'Evento',
  other: 'Altro',
};


// Cliente brief (coerente con ClienteBrief backend)
export interface LeadClienteBrief {
  id: string;
  nome?: string | null;
  cognome?: string | null;
  ragione_sociale?: string | null;
  tipo: string;
  email?: string | null;
  denominazione_completa?: string;
}

export interface LeadBase {
  nome?: string | null;
  cognome?: string | null;
  referente_interno?: string | null;
  ragione_sociale?: string | null;
  tipo: LeadTipo;

  email?: string | null;
  telefono?: string | null;
  pec?: string | null;

  codice_fiscale?: string | null;
  partita_iva?: string | null;

  indirizzo?: string | null;
  citta?: string | null;
  provincia?: string | null;
  cap?: string | null;
  latitudine?: number | null;
  longitudine?: number | null;

  stato: LeadStato;
  score: number;
  agente_id?: string | null;
  note?: string | null;

  source: LeadSource;
  source_detail?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;

  privacy: LeadPrivacy;
  consenso_marketing: LeadPrivacy;
  consenso_terzi: LeadPrivacy;
  consenso_profilazione: LeadPrivacy;

  prossima_azione_at?: string | null;
}

export interface LeadResponse extends LeadBase {
  id: string;
  tenant_id: string;

  ultimo_contatto_at?: string | null;
  convertito_cliente_id?: string | null;
  convertito_at?: string | null;

  convertito_cliente?: LeadClienteBrief | null;
  agente_nome?: string | null;

  created_at: string;
  updated_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  is_deleted: boolean;

  denominazione_completa: string;
}

export interface LeadBrief {
  id: string;
  nome?: string | null;
  cognome?: string | null;
  ragione_sociale?: string | null;
  tipo: LeadTipo;
  email?: string | null;
  telefono?: string | null;
  stato: LeadStato;
  source: LeadSource;
  agente_id?: string | null;
  agente_nome?: string | null;
  ultimo_contatto_at?: string | null;
  score: number;
  denominazione_completa: string;
}

export interface LeadCreate {
  nome?: string;
  cognome?: string;
  referente_interno?: string | null;
  ragione_sociale?: string;
  tipo: LeadTipo;

  email?: string;
  telefono?: string;
  pec?: string;

  codice_fiscale?: string;
  partita_iva?: string;

  indirizzo?: string;
  citta?: string;
  provincia?: string;
  cap?: string;

  stato?: LeadStato;
  score?: number;
  agente_id?: string | null;
  note?: string;

  source?: LeadSource;
  source_detail?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;

  privacy?: LeadPrivacy;
  consenso_marketing?: LeadPrivacy;
  consenso_terzi?: LeadPrivacy;
  consenso_profilazione?: LeadPrivacy;

  prossima_azione_at?: string | null;
}

export type LeadUpdate = Partial<LeadCreate>;

export interface LeadFilters {
  page?: number;
  limit?: number;
  stato?: LeadStato[];
  source?: LeadSource[];
  agente_id?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  tag_ids?: string[];
}

export interface LeadListResponse extends PaginatedResponse<LeadResponse> {}

export interface LeadActivityResponse {
  id: string;
  lead_id: string;
  tenant_id: string;
  type: LeadActivityType;
  subject?: string | null;
  body?: string | null;
  happened_at: string;
  metadata: Record<string, unknown>;
  created_at: string;
  created_by?: string | null;
  is_deleted: boolean;
}

export interface LeadActivityCreate {
  type: LeadActivityType;
  subject?: string;
  body?: string;
  happened_at?: string;
  metadata?: Record<string, unknown>;
}

export interface LeadActivityListResponse extends PaginatedResponse<LeadActivityResponse> {}

export interface LeadConvertRequest {
  strategia: 'new' | 'existing';
  cliente_id?: string;
  copia_activity?: boolean;
}

export interface LeadConvertResponse {
  lead_id: string;
  cliente_id: string;
  strategia: 'new' | 'existing';
  duplicati_trovati: LeadClienteBrief[];
  attivita_copiate: number;
}

export interface LeadConvertPreview {
  lead_id: string;
  duplicati_trovati: LeadClienteBrief[];
}

export interface LeadAssignRequest {
  agente_id: string | null;
}

export interface LeadStats {
  totale: number;
  per_stato: Record<string, number>;
  per_source: Record<string, number>;
  conversion_rate: number;
  giorni_ultima_attivita: number | null;
}

export interface LeadPipelineResponse {
  data: Record<LeadStato, LeadBrief[]>;
}

export function leadDenominazione(lead: Pick<LeadBase, 'tipo' | 'nome' | 'cognome' | 'ragione_sociale' | 'email'>): string {
  if (lead.tipo === 'azienda') {
    return (lead.ragione_sociale || lead.nome || '').trim();
  }
  const full = `${lead.nome || ''} ${lead.cognome || ''}`.trim();
  return full || lead.ragione_sociale || lead.email || '';
}
